import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectSpread from "@babel/runtime/helpers/objectSpread";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/**
 * Data structure manipulation tools.
 *
 * @module struct
 * @license Apache-2.0
 * @author drmats
 */

import { choose, curry, flow, Y } from "./func";
import { empty, quote } from "./string";
import { isArray, isFunction, isObject } from "./type";
import { handleException } from "./utils";
export var access = function access() {
  var o = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var path = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var def = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : undefined;
  return handleException(function () {
    return path.reduce(function (acc, p) {
      return acc[p];
    }, o) || def;
  }, function () {
    return def;
  });
};
export var clone = flow(JSON.stringify, JSON.parse);
export var hashAccessor = function hashAccessor() {
  return function (n) {
    return choose([isObject(n), isArray(n)].map(function (v) {
      return v ? 1 : 0;
    }).join(empty()), {
      "10": function _() {
        return Object.keys(n).map(function (k) {
          return [n[k], [k]];
        });
      },
      "01": function _() {
        return n.map(function (v, i) {
          return [v, [i]];
        });
      }
    }, function () {
      return [];
    });
  };
};
export var keyAccessor = function keyAccessor() {
  for (var _len = arguments.length, path = new Array(_len), _key = 0; _key < _len; _key++) {
    path[_key] = arguments[_key];
  }

  return path.length > 0 ? function (n) {
    return access(n, path, []).map(function (c, i) {
      return [c, [].concat(path, [i])];
    });
  } : hashAccessor();
};
export var dfs = function dfs() {
  var tree = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var f = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : function (_accs, node, _path, _position) {
    return node;
  };
  var children = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : keyAccessor();

  var bquote = function bquote(x) {
    return quote(typeof x, "[]");
  };

  if (!isObject(tree) || !isFunction(f) || !isFunction(children)) throw new TypeError("struct.dfs() expected object and 2 functions, " + "got ".concat(bquote(tree), ", ").concat(bquote(f), " and ").concat(bquote(children)));
  return Y(function (aux) {
    return function (node, path, position) {
      return f(children(node).map(function (_ref, p) {
        var _ref2 = _slicedToArray(_ref, 2),
            child = _ref2[0],
            childPath = _ref2[1];

        return aux(child, path.concat(childPath), p);
      }), node, path, position);
    };
  })(tree, [], 0);
};
export var dict = function dict(entries) {
  return entries.reduce(function (acc, _ref3) {
    var _ref4 = _slicedToArray(_ref3, 2),
        k = _ref4[0],
        v = _ref4[1];

    return _objectSpread({}, acc, _defineProperty({}, k, v));
  }, {});
};
export var objectMap = curry(function (o, f) {
  var bquote = function bquote(x) {
    return quote(typeof x, "[]");
  };

  if (!isObject(o) || !isFunction(f)) throw new TypeError("struct.objectMap() expected object and function, " + "got ".concat(bquote(o), " and ").concat(bquote(f)));
  return dict(Object.entries(o).map(function (kv) {
    return f.call(o, kv);
  }));
});
export var objectReduce = curry(function (o, f, init) {
  var bquote = function bquote(x) {
    return quote(typeof x, "[]");
  };

  if (!isObject(o) || !isFunction(f)) throw new TypeError("struct.objectReduce() expected object and function, " + "got ".concat(bquote(o), " and ").concat(bquote(f)));
  return Object.entries(o).reduce(function (acc, kv) {
    return f.call(o, acc, kv);
  }, init);
});
export var swap = function swap(o) {
  return objectMap(o, function (_ref5) {
    var _ref6 = _slicedToArray(_ref5, 2),
        k = _ref6[0],
        v = _ref6[1];

    return [v, k];
  });
};